import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormControl } from '@angular/forms';
import {
  accountsFormRules,
  sharedFormValidator,
} from '@credit-acceptance/shareable-utilities';
import { adobeAnalytics } from 'adobe-analytics';
import { isEqual } from 'lodash';
import { Subscription, takeUntil, tap, zip } from 'rxjs';

import { AWSService } from '../services/aws.service';
import { Unsubscribe } from '../shared/classes/unsubscribe.class';
import { SiblingValidationService } from '../validators/sibling-validation.service';

export const NO_ACCOUNT = 'No Account';

@Component({
  selector: 'capq-accounts',
  templateUrl: './accounts.component.html',
  styleUrls: ['./accounts.component.scss'],
})
export class AccountsComponent
  extends Unsubscribe
  implements OnInit, OnDestroy
{
  @Output() activeStep: EventEmitter<number> = new EventEmitter<number>();
  @Input() adobeScriptLoaded = false;

  initState = {
    bankingType: new FormControl([] as string[]),
  };
  accountsForm = this.formBuilder.group(this.initState);

  selectedAccounts = [];
  accountTypes = [
    {
      id: 1,
      displayName: 'Checking account',
      value: 'Checking Account',
      src: 'assets/images/icn_checking.svg',
      isSelected: false,
      isOnMouse: false,
    },
    {
      id: 2,
      displayName: 'Savings account',
      value: 'Savings Account',
      src: 'assets/images/icn_savings.svg',
      isSelected: false,
      isOnMouse: false,
    },
    {
      id: 3,
      displayName: 'Prepaid debit card',
      value: 'Prepaid Debit Card',
      src: 'assets/images/icn_debit.svg',
      isSelected: false,
      isOnMouse: false,
    },
  ];
  nonBanking = false;
  globalText: any;
  formFieldData: any;
  formIsIncomplete = true;
  isWaiting = false;
  noSelection = false;
  pageViewFired = false;
  formStartEventEmitted = false;
  validationSubscription: Subscription | null = null;

  constructor(
    private formBuilder: FormBuilder,
    private readonly awsService: AWSService,
    private siblingValidationService: SiblingValidationService,
    private cdRef: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.accountsForm.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        if (this.adobeScriptLoaded && !this.pageViewFired) {
          adobeAnalytics.emitEvent('cac-page-view', 'accounts', {
            form_design: 'v 1.0',
            section_name: 'accounts',
            form_submission_id: '',
            dealer_list: '',
            form_submission_status: '',
            event_type: 'cac-page-view',
          });
          this.pageViewFired = true;
        }
        if (
          !isEqual(this.initState, this.accountsForm.getRawValue()) &&
          !this.formStartEventEmitted &&
          this.adobeScriptLoaded
        ) {
          this.formStartEventEmitted = true;
          adobeAnalytics.emitFormStart('accounts');
        }
      });

    this.validationSubscription = this.siblingValidationService.validate$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.noSelection = false;
        const formKeys = Object.keys(this.accountsForm.controls);
        for (const key of formKeys) {
          this.accountsForm.controls[
            key as keyof typeof accountsFormRules
          ]?.markAsTouched();
          const { value } =
            this.accountsForm.controls[key as keyof typeof accountsFormRules];
          if (Array.isArray(value) && value.length > 0) {
            this.validate(value, key as keyof typeof accountsFormRules);
          } else {
            this.noSelection = true;
            this.accountsForm.controls.bankingType.setErrors({
              isNotEmptyList: 'List cannot be empty',
            });
          }
        }
        this.cdRef.detectChanges();
        this.siblingValidationService.reportValidation(
          4,
          this.accountsForm.getRawValue(),
          this.accountsForm.valid
        );
      });
    zip([
      this.awsService.prequalifiedJSONData$.pipe(
        tap((data: any) => {
          if (data) {
            this.globalText = data;
            this.formFieldData = data.accounts?.formsFields;
            this.initForm();
          }
        })
      ),
    ])
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.initForm();
      });

    // FIXME: enable adobe events once requirements are provided
    // this.adobeScriptLoaded &&
    //   adobeAnalytics.emitEvent('cac-page-view', 'accounts', {
    //     form_design: 'v 1.0',
    //     section_name: 'accounts',
    //     form_submission_id: (applicationId() || '').toString(),
    //     dealer_list: '',
    //     form_submission_status: '',
    //     event_type: 'cac-page-view',
    //   });
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.validationSubscription?.unsubscribe();
  }

  initForm(): void {
    this.accountsForm.valueChanges
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(() => {
        this.adobeScriptLoaded && adobeAnalytics.emitFormStart('accounts');
        this.noSelection = false;
        const entries = Object.entries(this.accountsForm.value as any) as [
          keyof typeof accountsFormRules,
          string[]
        ][];
        const missingValue = entries.find(
          ([key, value]) => !value && accountsFormRules[key].required
        );
        if (missingValue) {
          this.formIsIncomplete = true;
        } else {
          for (const [key, value] of entries) {
            this.validate(value, key, true);
          }
        }
      });
  }

  validate(
    value: string[],
    key: keyof typeof accountsFormRules,
    softCheck?: boolean
  ) {
    const { errors } = sharedFormValidator('accountsFormRules', key, value);
    if (errors && errors.length) {
      const errorObject: { [key: string]: string } = {};
      for (const err of errors) {
        errorObject[err.rule] = err.message;
      }
      softCheck
        ? (this.formIsIncomplete = true)
        : this.accountsForm.controls[
            key as keyof typeof this.accountsForm.controls
          ].setErrors(errorObject);
    } else {
      softCheck
        ? (this.formIsIncomplete = false)
        : this.accountsForm.controls[
            key as keyof typeof this.accountsForm.controls
          ].setErrors(null);
    }
  }

  toggleAccount(id: number) {
    this.nonBanking = false;
    const currentValue = this.accountsForm.controls.bankingType.value || [];
    const control = this.accountsForm.controls.bankingType;
    this.accountTypes.forEach((element) => {
      if (element.id === id) {
        element.isSelected = !element.isSelected;
        element.isSelected
          ? control.setValue([
              ...currentValue.filter((el: string) => el !== NO_ACCOUNT),
              element.value,
            ])
          : control.setValue([
              ...currentValue.filter((el: string) => el !== element.value),
            ]);
        element.isOnMouse = false;
      }
    });
  }

  mouseOver(id: number) {
    this.accountTypes.map((element) => {
      if (element.id === id) {
        element.isOnMouse = true;
      }
    });
  }

  mouseOut(id: number) {
    this.accountTypes.map((element) => {
      if (element.id === id) {
        element.isOnMouse = false;
      }
    });
  }

  handleNonBankingCheckboxToggle(isNonBanking: boolean) {
    if (isNonBanking) {
      this.accountsForm.controls.bankingType.setValue([NO_ACCOUNT]);
    } else {
      this.formIsIncomplete = true;
    }
    this.accountTypes.forEach((element) => {
      element.isSelected = false;
    });
  }

  handleEnterKeyPress($event: Event) {
    $event.preventDefault();
    this.nonBanking = !this.nonBanking;
    this.handleNonBankingCheckboxToggle(this.nonBanking);
  }
}
